import {
  setWorkstationPong,
  addPongHistoryEntry
} from "~/features/andon/andon.slice";

import { PongData } from "~/features/andon/andon.types";
import { hubs } from "~/lib/signalr";
import { HeartbeatEvent } from "~/types/api";

import { collectPongData } from "./heartbeat.logic";
// when receiving a ping, respond with a pong with collected pongData and own workstation device id
export async function handleHeartbeatPing({
  data,
  siteWorkstationId,
  workstationDeviceId
}: {
  data: HeartbeatEvent.Ping;
  siteWorkstationId: string;
  workstationDeviceId: string | null;
}) {
  const { targetWorkstationId, timePingSent } = data.item;
  // If this ping is for another workstation, ignore
  if (siteWorkstationId !== targetWorkstationId && targetWorkstationId !== "") {
    return;
  }

  const pongData: PongData = await collectPongData({
    workstationDeviceId
  });

  const pongEvent: HeartbeatEvent.Pong = {
    case: "Pong",
    item: {
      connection: pongData,
      sourceWorkstationId: siteWorkstationId,
      timePingSent: timePingSent // echo the original ping time
    }
  };
  hubs.send.next(pongEvent);
}

// when listening for and receiving a pong, add pong to pong data and history
export function handleHeartbeatPong({
  data,
  dispatch,
  batchTimestamp
}: {
  data: HeartbeatEvent.Pong;
  dispatch: (action: unknown) => void;
  batchTimestamp: string | null;
}) {
  const now = new Date();
  const sentTime = new Date(data.item.timePingSent);
  const pongLoopTime = now.getTime() - sentTime.getTime();
  const rawPongData = data.item.connection as Partial<PongData> | undefined;

  if (!rawPongData) return;

  const pongData: PongData = {
    userAgent: rawPongData.userAgent ?? "",
    userAgentParsed: rawPongData.userAgentParsed ?? {
      browser: "",
      version: "",
      os: "",
      platform: "",
      isMobile: false
    },
    cpu: rawPongData.cpu ?? { cores: 0 },
    fps: rawPongData.fps ?? 0,
    network: rawPongData.network ?? {
      effectiveType: "",
      rtt: 0,
      downlink: 0
    },
    memory: rawPongData.memory ?? {
      usedJSHeapSize: 0,
      totalJSHeapSize: 0,
      jsHeapSizeLimit: 0
    },
    userAgentLowEntropy: rawPongData.userAgentLowEntropy ?? {
      brands: [],
      mobile: false,
      platform: ""
    },
    userAgentHighEntropy: rawPongData.userAgentHighEntropy ?? {
      architecture: "",
      bitness: "",
      model: "",
      platform: "",
      platformVersion: "",
      uaFullVersion: "",
      fullVersionList: []
    },
    screen: rawPongData.screen ?? {
      width: 0,
      height: 0,
      availWidth: 0,
      availHeight: 0,
      pixelRatio: 0,
      innerWidth: 0,
      innerHeight: 0
    },
    timeZone: rawPongData.timeZone ?? "",
    timeZoneOffsetMinutes: rawPongData.timeZoneOffsetMinutes ?? 0,
    workstationDeviceId: rawPongData.workstationDeviceId ?? "no workstation",
    clientVersion: rawPongData.clientVersion ?? "",
    serverVersion: rawPongData.serverVersion ?? null,
    navigation: rawPongData.navigation ?? {
      type: "unknown",
      transferSize: 0,
      duration: 0
    }
  };

  dispatch(
    setWorkstationPong({
      workstationId: data.item.sourceWorkstationId,
      pongData,
      pongLoopTime,
      batchTimestamp: batchTimestamp,
      timePingSent: new Date(data.item.timePingSent).toISOString()
    })
  );

  dispatch(
    addPongHistoryEntry({
      workstationId: data.item.sourceWorkstationId,
      pongData,
      loopTime: pongLoopTime
    })
  );
}
